// AddressForm.tsx
import { MdClose, MdCheck } from "react-icons/md";
import styles from "./AddressForm.module.scss";
import { FullAddress } from "../../../common/types/types";
import { useTheme } from "../../../common/hooks/useTheme";
import { Input } from "../../../common/components/input/Input";
import { GMapInput } from "../../../common/components/gMapInput/GMapInput";
import { useAddressForm } from "./useAddresForm";
import { store } from "../../../store/store";

interface AddressFormProps {
  getList: () => void;
  onSubmit?: (data: FullAddress) => void;
  onClose?: () => void;
}

export default function AddressForm({
  onSubmit,
  onClose,
  getList,
}: AddressFormProps) {
  const { theme, isDark } = useTheme();

  const {
    data,
    isValid,
    inputHandler,
    setDirty,
    getValidationResult,
    addressNotValid,
    formAction,
    closeForm,
    setData,
  } = useAddressForm(getList, onClose, onSubmit);

  const isAnonymous =
    store.getState().executor.isExecutor &&
    !store.getState().executor.clientUserId;

  return (
    <div
      className={styles.container}
      style={{
        backgroundColor: theme.background,
        borderColor: theme.border,
      }}
    >
      <div className={styles.header}>
        <h3 className={styles.title} style={{ color: theme.text }}>
          Новый адрес
        </h3>
        {onClose && !isAnonymous && (
          <button className={styles.closeButton} onClick={closeForm}>
            <MdClose size={16} color={isDark ? "#aaa" : "#666"} />
          </button>
        )}
      </div>

      {!isAnonymous && (
        <div className={styles.formRow}>
          <Input
            value={data.name}
            onChange={(value) => inputHandler(value, "name", "string")}
            fieldName="name"
            placeholder="Дом, Работа..."
            label="Название *"
            validationResult={getValidationResult("name")}
            onBlur={() => setDirty("name")}
          />
        </div>
      )}

      <GMapInput
        fieldName="address"
        setData={setData}
        data={data}
        placeHolder="Введите адрес"
        required
      />

      <div className={styles.rowFields}>
        <div className={styles.halfField}>
          <Input
            value={data.entrance || ""}
            onChange={(value) => inputHandler(value, "entrance", "text")}
            fieldName="entrance"
            placeholder="Подъезд"
            label="Подъезд"
            validationResult={getValidationResult("entrance")}
            onBlur={() => setDirty("entrance")}
          />
        </div>
        <div className={styles.halfField}>
          <Input
            value={data.floor || ""}
            onChange={(value) => inputHandler(value, "floor", "text")}
            fieldName="floor"
            placeholder="Этаж"
            label="Этаж"
            validationResult={getValidationResult("floor")}
            onBlur={() => setDirty("floor")}
          />
        </div>
      </div>

      <div className={styles.rowFields}>
        <div className={styles.halfField}>
          <Input
            value={data.apartment || ""}
            onChange={(value) => inputHandler(value, "apartment", "text")}
            fieldName="apartment"
            placeholder="Квартира/офис"
            label="Квартира/офис"
            validationResult={getValidationResult("apartment")}
            onBlur={() => setDirty("apartment")}
          />
        </div>
        <div className={styles.halfField}>
          <Input
            value={data.intercom || ""}
            onChange={(value) => inputHandler(value, "intercom", "text")}
            fieldName="intercom"
            placeholder="Домофон"
            label="Домофон"
            validationResult={getValidationResult("intercom")}
            onBlur={() => setDirty("intercom")}
          />
        </div>
      </div>

      <div className={styles.formRow}>
        <Input
          value={data.description || ""}
          onChange={(value) => inputHandler(value, "description", "text")}
          fieldName="description"
          placeholder="Дополнительная информация"
          label="Комментарий"
          multiline
          rows={3}
          validationResult={getValidationResult("description")}
          onBlur={() => setDirty("description")}
        />
      </div>

      {!isAnonymous && (
        <button
          className={styles.checkboxRow}
          onClick={() => inputHandler(!data.isGeneral, "isGeneral", "checkbox")}
        >
          <div
            className={`${styles.checkbox} ${data.isGeneral ? styles.checked : ""}`}
          >
            {data.isGeneral && <MdCheck size={12} color="#fff" />}
          </div>
          <span className={styles.checkboxLabel} style={{ color: theme.text }}>
            Основной адрес
          </span>
        </button>
      )}

      <button
        className={styles.saveButton}
        onClick={formAction}
        disabled={!isValid || addressNotValid}
      >
        <span className={styles.saveButtonText}>Сохранить</span>
      </button>
    </div>
  );
}
